import * as React from 'react';

import type { BFFAssetResponse, BFFResponse } from '../../store/types/store-types';

import { DataTable, type DataTableHeader, type DataTableRow } from './data-table';

const headers: DataTableHeader[] = [
	{ key: 'variant', content: 'Variant' },
	{ key: 'extension', content: 'Extension' },
	{ key: 'sgCode', content: 'SG Code' },
	{ key: 'salesgroup', content: 'Salesgroup' },
	{ key: 'carline', content: 'Carline' },
	{ key: 'trimline', content: 'Trimline' },
	{ key: 'color', content: 'Color' },
	{ key: 'rim', content: 'Rim' },
	{ key: 'interior', content: 'Interior' }
];

interface VariantTableProps {
	assets: BFFResponse['assets'] | null | undefined;
	variantKeys: string[];
}

const VariantList = ({ data }: { data: Record<string, { name: string }> }) => {
	return (
		<div className="cell-list">
			{Object.entries(data).map(([code, { name }]) => (
				<div key={code} className="cell-list-item">
					<b>{code}</b> — {name.replace(/\s*\/\s*/g, ' | ')}
				</div>
			))}
		</div>
	);
};

function renderVariantCell(data: Record<string, { name: string }> | undefined) {
	if (!data || Object.keys(data).length === 0) {
		return '—';
	}

	return <VariantList data={data} />;
}

function buildDuplicateIndexMap(
	assets: BFFResponse['assets'] | null | undefined,
	variantKeys: string[]
): Map<string, number> {
	const totals = new Map<string, number>();
	const counters = new Map<string, number>();
	const indices = new Map<string, number>();

	if (!assets) return indices;

	for (const key of variantKeys) {
		const mbvKey = assets[key]?.vehicleConfiguration.modelKey;
		if (!mbvKey) continue;
		totals.set(mbvKey, (totals.get(mbvKey) ?? 0) + 1);
	}

	for (const key of variantKeys) {
		const mbvKey = assets[key]?.vehicleConfiguration.modelKey;
		if (!mbvKey) continue;
		if ((totals.get(mbvKey) ?? 0) > 1) {
			const next = (counters.get(mbvKey) ?? 0) + 1;
			counters.set(mbvKey, next);
			indices.set(key, next);
		}
	}

	return indices;
}

function createRow(key: string, asset: BFFAssetResponse, duplicateIndex: number | undefined): DataTableRow {
	const { vehicleConfiguration, trimLayerDetails, thumbnailColor, thumbnailInterior, thumbnailRim } = asset;

	const carlineName = trimLayerDetails?.model?.carlineName || vehicleConfiguration.familyName || '—';
	const salesgroupName = trimLayerDetails?.salesgroupName || vehicleConfiguration.familyName || '—';
	const trimlineName = trimLayerDetails?.model?.equipmentLine || vehicleConfiguration.trimName || '—';
	const variantBase = vehicleConfiguration.modelKey || key;
	const variantDisplay =
		duplicateIndex && vehicleConfiguration.modelKey ? `${variantBase}[${duplicateIndex}]` : variantBase;
	const sgCode = vehicleConfiguration.salesgroupCode || '—';
	const extensionLabel =
		vehicleConfiguration.extensions && vehicleConfiguration.extensions.length > 0
			? vehicleConfiguration.extensions.join('-')
			: '—';

	return {
		key,
		cells: [
			{ key: 'variant', content: variantDisplay },
			{ key: 'extension', content: extensionLabel },
			{ key: 'sgCode', content: sgCode },
			{ key: 'salesgroup', content: salesgroupName },
			{ key: 'carline', content: carlineName },
			{ key: 'trimline', content: trimlineName },
			{ key: 'color', content: renderVariantCell(thumbnailColor) },
			{ key: 'rim', content: renderVariantCell(thumbnailRim) },
			{ key: 'interior', content: renderVariantCell(thumbnailInterior) }
		]
	};
}

export function VariantTable({ assets, variantKeys }: VariantTableProps) {
	const duplicateIndices = buildDuplicateIndexMap(assets, variantKeys);

	const rows: DataTableRow[] =
		assets && variantKeys.length > 0
			? variantKeys.reduce<DataTableRow[]>((accumulator, key) => {
					const asset = assets?.[key];

					if (!asset) {
						return accumulator;
					}

					accumulator.push(createRow(key, asset, duplicateIndices.get(key)));
					return accumulator;
				}, [])
			: [];

	const fallback = assets ? 'No variants available.' : 'No BFF data available.';

	return <DataTable headers={headers} rows={rows} fallback={fallback} />;
}
